<template>
  <footer class="py-12 border-t border-slate-200 dark:border-slate-800 bg-[#f8f6f6] dark:bg-[#221610]" role="contentinfo">
    <div class="max-w-7xl mx-auto px-6">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
        <div class="md:col-span-2">
          <div class="flex items-center gap-2 mb-6">
            <button
              v-if="canOpenOwnerLogoModal"
              type="button"
              class="min-h-[44px] min-w-[44px] w-10 h-10 shrink-0 rounded overflow-hidden bg-slate-200 dark:bg-slate-700 inline-flex items-center justify-center border-0 cursor-pointer p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              :style="{ '--tw-ring-color': 'var(--rms-t4-primary,#ec5b13)' }"
              :aria-label="$t('public.ownerEditLogoAria')"
              @click="openOwnerLogoModal"
            >
              <img
                v-if="logoUrl"
                :src="logoUrl"
                :alt="restaurantName || 'Restaurant'"
                class="w-full h-full object-contain"
              >
              <span v-else class="material-symbols-outlined text-2xl text-slate-500" aria-hidden="true" :style="{ color: 'var(--rms-t4-primary,#ec5b13)' }">storefront</span>
            </button>
            <div v-else class="w-10 h-10 shrink-0 rounded overflow-hidden bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
              <img
                v-if="logoUrl"
                :src="logoUrl"
                :alt="restaurantName || 'Restaurant'"
                class="w-full h-full object-contain"
              >
              <span class="material-symbols-outlined text-2xl text-slate-500" aria-hidden="true" :style="{ color: 'var(--rms-t4-primary,#ec5b13)' }">storefront</span>
            </div>
            <h2 class="text-xl font-black tracking-tight text-slate-900 dark:text-slate-100">{{ restaurantName || 'Restaurant' }}</h2>
          </div>
          <p class="text-slate-600 dark:text-slate-400 max-w-sm mb-6 text-sm">
            {{ $t('public.t3FooterTagline') }}
          </p>
        </div>
        <div>
          <h4 class="text-xs font-bold uppercase tracking-widest mb-6 text-slate-900 dark:text-slate-100">{{ $t('public.t3QuickLinks') }}</h4>
          <ul class="space-y-4 text-sm text-slate-600 dark:text-slate-400">
            <li><a href="#menu" class="hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 rounded" :style="{ '--tw-ring-color': 'var(--rms-t4-primary,#ec5b13)' }">{{ viewListLabel || $t('public.viewMenu') }}</a></li>
            <li v-if="feedbackAvailable"><a href="#reviews" class="hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 rounded" :style="{ '--tw-ring-color': 'var(--rms-t4-primary,#ec5b13)' }">{{ $t('public.reviewsAndFeedback') }}</a></li>
          </ul>
        </div>
        <div>
          <h4 class="text-xs font-bold uppercase tracking-widest mb-6 text-slate-900 dark:text-slate-100">{{ $t('public.contactUs') }}</h4>
          <p class="text-slate-600 dark:text-slate-400 text-sm">{{ $t('public.t3FooterContact') }}</p>
        </div>
      </div>
      <div class="pt-8 border-t border-slate-200 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4 text-xs font-medium tracking-wide text-slate-500">
        <p>© {{ currentYear }} {{ restaurantName || 'Restaurant' }}</p>
        <div class="flex gap-6">
          <button
            type="button"
            class="hover:opacity-90 transition-opacity min-h-[44px] inline-flex items-center justify-center -m-2 p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rms-t4-primary,#ec5b13)] rounded"
            @click="openLegalModal('terms')"
          >
            {{ $t('app.termsOfService') }}
          </button>
          <button
            type="button"
            class="hover:opacity-90 transition-opacity min-h-[44px] inline-flex items-center justify-center -m-2 p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rms-t4-primary,#ec5b13)] rounded"
            @click="openLegalModal('privacy')"
          >
            {{ $t('app.privacyPolicy') }}
          </button>
          <button
            type="button"
            class="hover:opacity-90 transition-opacity min-h-[44px] inline-flex items-center justify-center -m-2 p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rms-t4-primary,#ec5b13)] rounded"
            @click="openCookiePrefs"
          >
            {{ $t('landing.footer.cookies') }}
          </button>
        </div>
      </div>
      <PublicFooterCredits v-if="showCredits" class="mt-4 text-slate-500 [&_a]:text-slate-500 [&_span]:text-slate-500 hover:[&_a]:text-slate-600" compact />
    </div>
    <LegalContentModal
      :open="legalModalOpen"
      :type="legalModalType"
      :locale="modalLocale"
      @close="closeLegalModal"
    />
  </footer>
</template>

<script setup>
import { ref, computed } from 'vue'
import LegalContentModal from '@/components/legal/LegalContentModal.vue'
import PublicFooterCredits from '@/components/public/PublicFooterCredits.vue'
import { usePublicOwnerLogoModal } from '@/composables/usePublicOwnerLogoModal'
import { useCookieConsentStore } from '@/stores/sitePrefs'

const { canOpen: canOpenOwnerLogoModal, openOwnerLogoModal } = usePublicOwnerLogoModal()
const cookieConsentStore = useCookieConsentStore()

defineProps({
  restaurantName: { type: String, default: '' },
  logoUrl: { type: String, default: '' },
  showCredits: { type: Boolean, default: true },
  modalLocale: { type: String, default: 'en' },
  viewListLabel: { type: String, default: '' },
  feedbackAvailable: { type: Boolean, default: false },
})

const legalModalOpen = ref(false)
const legalModalType = ref('terms')

const currentYear = computed(() => new Date().getFullYear())

function openLegalModal(type) {
  legalModalType.value = type
  legalModalOpen.value = true
}

function closeLegalModal() {
  legalModalOpen.value = false
}

function openCookiePrefs() {
  cookieConsentStore.openPrefs()
}
</script>
